/**
 * <back-in-stock-form>
 *
 * Wires the "Notify me when back in stock" form to Klaviyo's client Back in
 * Stock API. Two calls are made:
 *
 *   1. POST /client/profiles/                — attaches the storefront locale
 *      (BCP 47, e.g. `en-FR`) to the profile so the Klaviyo flow can split the
 *      Back in Stock email by language.
 *   2. POST /client/back-in-stock-subscriptions/ — creates the subscription
 *      for the exact catalog variant the customer is looking at.
 *
 * The variant id is re-read from the buy-buttons form on each submit so
 * switching variants in the picker before opening the modal Just Works.
 *
 * Both endpoints are CORS-open on Klaviyo's side.
 */

const KLAVIYO_REVISION = '2025-07-15';
const KLAVIYO_ENDPOINT_PROFILE = 'https://a.klaviyo.com/client/profiles/';
const KLAVIYO_ENDPOINT_BIS = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

function readCompanyIdFromScriptTag() {
  const scriptEl = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
  if (!scriptEl) return null;
  const match = scriptEl.src.match(/onsite\/js\/([^/]+)\//);
  return match ? match[1] : null;
}

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-back-in-stock-form]');
    this.messageEl = this.querySelector('[data-back-in-stock-message]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');

    if (!this.form) return;

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  get companyId() {
    return this.dataset.companyId || readCompanyIdFromScriptTag();
  }

  get locale() {
    return this.dataset.locale || document.documentElement.lang || '';
  }

  get variantId() {
    const selector = this.dataset.variantInputSelector;
    if (selector) {
      const input = document.querySelector(selector);
      if (input?.value) return input.value;
    }
    return this.dataset.variantId || '';
  }

  get catalogVariantId() {
    return `$shopify:::$default:::${this.variantId}`;
  }

  async handleSubmit(event) {
    event.preventDefault();

    const email = this.form.querySelector('input[name="email"]')?.value?.trim();

    if (!email) {
      this.showMessage(this.t('error_missing_email'), 'error');
      return;
    }

    if (!this.companyId) {
      this.showMessage(this.t('error_config'), 'error');
      return;
    }

    if (!this.variantId) {
      this.showMessage(this.t('error_missing_variant'), 'error');
      return;
    }

    this.setBusy(true);
    this.showMessage('', null);

    try {
      await this.identifyProfile(email);
      await this.subscribeToBackInStock(email);
      this.showMessage(this.t('success'), 'success');
      this.form.reset();
    } catch (err) {
      console.error('[back-in-stock] submission failed', err);
      this.showMessage(this.t('error_generic'), 'error');
    } finally {
      this.setBusy(false);
    }
  }

  async identifyProfile(email) {
    const attributes = { email };

    if (this.locale) {
      // `locale` is a native Klaviyo profile attribute (BCP 47) and is what the
      // Klaviyo UI shows under Profile details. The custom property of the same
      // name is written alongside it because the flow Charlotte is building
      // filters on the property — drop it once the flow reads the native field.
      attributes.locale = this.locale;
      attributes.properties = { locale: this.locale };
    }

    const res = await fetch(`${KLAVIYO_ENDPOINT_PROFILE}?company_id=${encodeURIComponent(this.companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({ data: { type: 'profile', attributes } }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`profile endpoint returned ${res.status}: ${text.slice(0, 200)}`);
    }
  }

  async subscribeToBackInStock(email) {
    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: this.catalogVariantId,
            },
          },
        },
      },
    };

    const res = await fetch(`${KLAVIYO_ENDPOINT_BIS}?company_id=${encodeURIComponent(this.companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`back-in-stock endpoint returned ${res.status}: ${text.slice(0, 200)}`);
    }
  }

  setBusy(isBusy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isBusy;
    this.submitButton.setAttribute('aria-busy', String(isBusy));
  }

  showMessage(text, status) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.hidden = !text;
    if (status) {
      this.messageEl.dataset.status = status;
    } else {
      delete this.messageEl.dataset.status;
    }
  }

  t(key) {
    const messages = this.messages || (this.messages = readMessages(this));
    return messages[key] || '';
  }
}

function readMessages(el) {
  const script = el.querySelector('script[data-back-in-stock-messages]');
  if (!script) return {};
  try {
    const parsed = JSON.parse(script.textContent);
    // Shopify's `| t` filter HTML-encodes apostrophes and quotes (they arrive
    // here as `&#39;`, `&quot;`…). Script tags don't decode entities and
    // JSON.parse doesn't touch HTML entities either, so we do it manually.
    return Object.fromEntries(
      Object.entries(parsed).map(([k, v]) => [k, decodeHtmlEntities(v)])
    );
  } catch (_) {
    return {};
  }
}

function decodeHtmlEntities(str) {
  if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
